import { useId, useRef, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { RequiredBadge } from "@/components/design/EntryFields";
import { Modal } from "@/components/design/Modal";
import { PlusButton } from "@/components/design/PlusButton";
import { ProgressBar } from "@/components/design/ProgressBar";
import { TextInput } from "@/components/design/TextField";
import { CreateListingDocument, SuggestListingRolesDocument, SuggestionStatus } from "@/graphql/generated/operations";
import { fromStoragePercent } from "@/lib/skill-levels";
import { userErrorMessage } from "@/lib/user-error";
import { throwIfRejected, ValidationRejected } from "@/pages/share/listings/listing-form";
import { forgetListings } from "@/pages/share/listings/use-listing";
import {
  addRole,
  applyRoleSuggestion,
  creationFailureIsCertain,
  initialGapPlan,
  listingDraftInput,
  pendingRoles,
  removeRole,
  renameRole,
  roleKeys,
  roleSuggestionInput,
  rolesReady,
  SUGGESTION_MAX_HINT,
  toggleRoleAssignment,
  unassignedKeys,
  type GapPlan,
  type GapRequirement,
  type RoleCreation,
} from "./gap-listing-plan";
import { MatchSkillsDiagram, type MatchSkillEntry } from "./requirements/MatchSkillsDiagram";
import { RequirementPillLock } from "./RequirementPillLock";
import { requirementPillClasses } from "./requirement-pill-classes";
import { labelForCondition } from "./match-result-derive";
import { useConditionLabelFormatters, useConditionLabelLookup } from "./result-panel-labels";
import { useValidationFallback } from "./use-match-editor";

const LISTINGS_PATH = "/share/listings";
const LINK = "rounded underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui";

/**
 * Die Zwischenansicht "Aus Lücken Stellen planen" (spark#1237): Lücken wählen, Rollen benennen und
 * zuordnen, optional eine Aufteilung vorschlagen lassen, dann Entwürfe anlegen und in die Verwaltung.
 * Der Zustand lebt nur hier: geschlossen ist der Plan weg, angelegte Entwürfe stehen in der Verwaltung.
 */
export function GapListingPlanner({ matchId, gaps, description, onClose }: {
  matchId: string;
  gaps: readonly GapRequirement[];
  /** Die fachliche Beschreibung des Matches, wörtlich für jeden Entwurf. */
  description: string;
  onClose: () => void;
}) {
  const intl = useIntl();
  const uid = useId();
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const fallback = useValidationFallback();
  const labelLookup = useConditionLabelLookup();
  const labelFormatters = useConditionLabelFormatters(intl);
  const labelOf = (gap: GapRequirement) => labelForCondition(gap.analysis.condition, labelLookup, labelFormatters);

  const newId = () => crypto.randomUUID();
  const [initial] = useState<GapPlan>(() => initialGapPlan(newId));
  const [plan, setPlan] = useState<GapPlan>(initial);

  const [hint, setHint] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestionNote, setSuggestionNote] = useState<string | null>(null);
  const [beforeSuggestion, setBeforeSuggestion] = useState<GapPlan | null>(null);

  const [createListing] = useMutation(CreateListingDocument);
  const creatingRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const [creations, setCreations] = useState<Record<string, RoleCreation>>({});
  const [createError, setCreateError] = useState<string | null>(null);

  const started = Object.keys(creations).length > 0;
  const locked = (roleId: string) => creations[roleId] != null && creations[roleId]!.state !== "FAILED";
  const pending = pendingRoles(plan, creations);
  const pendingReady = rolesReady(plan, pending);
  const unassigned = unassignedKeys(plan);
  const suggestionInput = roleSuggestionInput({ plan, gaps, labelOf, matchId, locale: intl.locale, hint });
  const busy = creating || suggesting;

  async function suggest() {
    if (!suggestionInput) return;
    setSuggesting(true);
    setSuggestionNote(null);
    try {
      const result = await apollo.query({
        query: SuggestListingRolesDocument,
        variables: { input: suggestionInput },
        fetchPolicy: "no-cache",
      });
      const answer = result.data?.suggestListingRoles;
      // Während der Anfrage ist jede Eingabe gesperrt (busy), der Plan also noch der gefragte.
      const current = plan;
      const next = answer?.status === SuggestionStatus.Ok ? applyRoleSuggestion(current, answer.roles, newId) : current;
      if (next === current) {
        setSuggestionNote(intl.formatMessage(answer?.status === SuggestionStatus.Unavailable
          ? { id: "match.gapListings.suggestUnavailable", defaultMessage: "Gerade kein Vorschlag möglich. Deine Rollen bleiben, wie sie sind." }
          : { id: "match.gapListings.suggestEmpty", defaultMessage: "Kein brauchbarer Vorschlag. Deine Rollen bleiben, wie sie sind." }));
        return;
      }
      setBeforeSuggestion(current);
      setPlan(next);
    } catch (e) {
      setSuggestionNote(userErrorMessage(intl, e));
    } finally {
      setSuggesting(false);
    }
  }

  async function create() {
    if (creatingRef.current) return;
    const todo = pendingRoles(plan, creations);
    if (todo.length === 0) return;
    creatingRef.current = true;
    setCreating(true);
    setCreateError(null);
    const next = { ...creations };
    try {
      // Erst alle prüfen: fehlt das Recht oder lehnt genesis einen Entwurf ab, entsteht keiner.
      for (const role of todo) {
        const checked = await createListing({
          variables: { input: { data: listingDraftInput(plan, role, gaps, description), dryRun: true } },
          // Die Probe antwortet mit einer Ausschreibung, die genesis zurückgerollt hat: nicht in den Cache.
          fetchPolicy: "no-cache",
        });
        throwIfRejected(checked.data?.createListing, fallback);
      }
    } catch (e) {
      setCreateError(e instanceof ValidationRejected ? e.message : userErrorMessage(intl, e));
      creatingRef.current = false;
      setCreating(false);
      return;
    }
    for (const role of todo) {
      try {
        const created = await createListing({
          variables: { input: { data: listingDraftInput(plan, role, gaps, description), dryRun: false } },
          update: forgetListings,
        });
        const payload = created.data?.createListing;
        throwIfRejected(payload, fallback);
        if (payload?.__typename !== "CreateListingResult") throw new Error("createListing returned no listing");
        next[role.id] = { state: "CREATED", listingId: payload.result.id };
      } catch (e) {
        next[role.id] = creationFailureIsCertain(e)
          ? { state: "FAILED", message: e instanceof Error ? e.message : fallback }
          : { state: "UNCERTAIN" };
        // Nach einem Fehlschlag nicht weiter: der Rest bleibt offen und lässt sich gezielt anlegen.
        break;
      } finally {
        setCreations({ ...next });
      }
    }
    creatingRef.current = false;
    setCreating(false);
    if (plan.roles.every((r) => next[r.id]?.state === "CREATED")) navigate(LISTINGS_PATH);
  }

  const gapByKey = new Map(gaps.map((g) => [g.key, g]));
  const multi = plan.roles.length > 1;
  const requirementWord = (gap: GapRequirement) => intl.formatMessage(gap.analysis.isMust
    ? { id: "match.result.ranking.must", defaultMessage: "Muss" }
    : { id: "match.result.ranking.optional", defaultMessage: "Kann" });
  const roleNumber = (index: number) =>
    intl.formatMessage({ id: "match.gapListings.roleNumber", defaultMessage: "Rolle {n}" }, { n: index + 1 });
  const roleName = (index: number) => plan.roles[index]!.title.trim() || roleNumber(index);

  const skillEntries: MatchSkillEntry[] = gaps.flatMap((gap) => gap.condition.__typename === "ExpertiseCondition"
    ? [{ id: gap.key, description: gap.condition.expertise, level: fromStoragePercent(gap.condition.level), requirement: gap.condition.requirement }]
    : []);
  const otherGaps = gaps.filter((gap) => gap.condition.__typename !== "ExpertiseCondition");

  function renderGap(gap: GapRequirement, inDiagram = false) {
    const blocked = gap.evidence === "OVERSHOOT" || busy;
    const label = labelOf(gap);
    const visibleLabel = inDiagram && gap.condition.__typename === "ExpertiseCondition" && gap.condition.operator === "GE"
      ? gap.condition.expertise : label;
    const only = plan.roles[0]!;
    const selected = only.keys.includes(gap.key);
    const isMust = gap.analysis.isMust;
    const glyph = <RequirementPillLock isMust={isMust} selected={!multi && selected} label={requirementWord(gap)} />;
    return (
      <div className="w-full min-w-0">
        {multi ? (
          <div role="group"
            className={`${requirementPillClasses({ isMust })} flex-wrap`}
            aria-label={intl.formatMessage({ id: "match.gapListings.chooseRole", defaultMessage: "Rolle für {label} wählen" }, { label })}>
            {glyph}
            <span className="min-w-0 whitespace-normal break-words">{visibleLabel}</span>
            {plan.roles.map((role, index) => {
              const on = role.keys.includes(gap.key);
              return (
                <Button key={role.id} variant={on ? "primary" : "chip"} size="compact" aria-pressed={on}
                  disabled={blocked || locked(role.id)}
                  onClick={() => setPlan((p) => toggleRoleAssignment(p, role.id, gap))}>
                  {roleNumber(index)}
                </Button>
              );
            })}
          </div>
        ) : (
          <button type="button"
            className={`${requirementPillClasses({ isMust, selected })} focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui disabled:pointer-events-none disabled:opacity-50`}
            aria-label={label}
            aria-pressed={selected} disabled={blocked || locked(only.id)}
            onClick={() => setPlan((p) => toggleRoleAssignment(p, only.id, gap))}>
            {glyph}
            <span className="min-w-0 whitespace-normal break-words">{visibleLabel}</span>
          </button>
        )}
        <GapMeta gap={gap} />
        {plan.selected.includes(gap.key) && !plan.roles.some((r) => r.keys.includes(gap.key)) ? (
          <span className="block smycles-field-meta text-ink">
            <FormattedMessage id="match.gapListings.notAssigned" defaultMessage="Keiner Rolle zugeordnet" />
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <Modal
      open
      id="match-gap-listings"
      large
      dismiss="explicit"
      dirty={plan !== initial || hint.trim() !== ""}
      preventClose={busy}
      onClose={onClose}
      title={<FormattedMessage id="match.gapListings.title" defaultMessage="Aus Lücken Stellen planen" />}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            <FormattedMessage id={started ? "common.close" : "common.cancel"} />
          </Button>
          {started ? (
            <Button variant="secondary" onClick={() => navigate(LISTINGS_PATH)} disabled={busy}>
              <FormattedMessage id="match.gapListings.toListings" defaultMessage="Zur Verwaltung" />
            </Button>
          ) : null}
          {pending.length > 0 ? (
            <Button onClick={() => void create()} disabled={busy || !pendingReady} aria-busy={creating}>
              <FormattedMessage
                id="match.gapListings.create"
                defaultMessage="{count, plural, one {Entwurf anlegen} other {# Entwürfe anlegen}}"
                values={{ count: pending.length }}
              />
            </Button>
          ) : null}
        </>
      }
    >
      {/* Das große Modal hält Titel und Fußzeile fest, scrollen muss sein Inhalt selbst. Der Überstand
          ins Body-Padding hält Ringe und KI-Schatten der Flächen innerhalb des Scroll-Clips. */}
      <div className="-mx-6 -mb-6 min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        <p className="max-w-[62ch] text-smycles-body text-ink">
          <FormattedMessage
            id="match.gapListings.intro"
            defaultMessage="Ordne Lücken per Klick einer Rolle zu. Angelegt werden nur Entwürfe, veröffentlicht wird nichts."
          />
        </p>

        <div className="mt-6 grid items-start gap-6 @lg:grid-cols-2">
          <div className="min-w-0 space-y-6">
            <section aria-labelledby={`${uid}-roles`} className="bg-page box-flat p-6">
              <BoxHeader title={<span id={`${uid}-roles`}>
                <FormattedMessage id="match.gapListings.roles" defaultMessage="Rollen" />
              </span>} />
              <div className="divide-y divide-ink/5">
                {plan.roles.map((role, index) => {
                  const creation = creations[role.id];
                  const frozen = locked(role.id) || busy;
                  const titleId = `${uid}-role-${role.id}`;
                  return (
                    <div key={role.id} className="py-6 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="smycles-field-label">
                          <label htmlFor={titleId}>{roleNumber(index)}</label>
                          <RequiredBadge />
                        </div>
                        {plan.roles.length > 1 && !locked(role.id) ? (
                          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setPlan((p) => removeRole(p, role.id))}
                            aria-label={intl.formatMessage({ id: "match.gapListings.removeRole", defaultMessage: "Rolle {n} entfernen" }, { n: index + 1 })}>
                            <FormattedMessage id="common.remove" defaultMessage="Entfernen" />
                          </Button>
                        ) : null}
                      </div>
                      <TextInput
                        id={titleId}
                        className="mt-1"
                        value={role.title}
                        required
                        disabled={frozen}
                        maxLength={200}
                        placeholder={intl.formatMessage({ id: "match.gapListings.rolePlaceholder", defaultMessage: "z. B. Backend-Entwicklung" })}
                        onChange={(e) => setPlan((p) => renameRole(p, role.id, e.target.value))}
                      />
                      <fieldset className="mt-4" disabled={frozen}>
                        <legend className="smycles-field-label">
                          <FormattedMessage id="match.gapListings.roleRequirements" defaultMessage="Anforderungen dieser Rolle" />
                        </legend>
                        {roleKeys(plan, role).length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {gaps.filter((g) => roleKeys(plan, role).includes(g.key)).map((gap) => (
                              <Button key={gap.key} variant="primary" size="compact" className="gap-2"
                                aria-label={intl.formatMessage(
                                  { id: "match.gapListings.removeAssignment", defaultMessage: "{label} aus {role} entfernen" },
                                  { label: labelOf(gap), role: roleName(index) })}
                                onClick={() => setPlan((p) => toggleRoleAssignment(p, role.id, gap))}>
                                {labelOf(gap)}
                                <span aria-hidden="true">×</span>
                              </Button>
                            ))}
                          </div>
                        ) : !creation ? (
                          <p className="mt-2 smycles-field-meta text-ink-muted">
                            <FormattedMessage id="match.gapListings.needsRequirement" defaultMessage="Ordne dieser Rolle per Klick eine Lücke zu." />
                          </p>
                        ) : null}
                      </fieldset>
                      {creation ? <CreationLine creation={creation} /> : null}
                    </div>
                  );
                })}
              </div>

              {!started ? (
                <PlusButton
                  className="mt-4"
                  label={<FormattedMessage id="match.gapListings.addRole" defaultMessage="Rolle hinzufügen" />}
                  disabled={busy}
                  onClick={() => setPlan((p) => addRole(p, newId))}
                />
              ) : null}

              {unassigned.length > 0 ? (
                <p className="mt-4 smycles-field-meta text-ink-muted">
                  <FormattedMessage
                    id="match.gapListings.unassigned"
                    defaultMessage="{count, plural, one {Eine gewählte Lücke ist} other {# gewählte Lücken sind}} keiner Rolle zugeordnet: {labels}. Ordne sie einer Rolle zu."
                    values={{ count: unassigned.length, labels: unassigned.map((k) => gapByKey.get(k)).filter(Boolean).map((g) => labelOf(g!)).join(", ") }}
                  />
                </p>
              ) : null}
            </section>

            {!started ? (
              <div className="bg-page box-flat smycles-ai-shadow p-6 transition focus-within:ring-1 focus-within:ring-ink-accent-ui">
                <label className="block">
                  <span className="mb-1 block text-smycles-micro uppercase tracking-[0.25em] text-ink-accent">
                    <FormattedMessage id="match.gapListings.hintLabel" defaultMessage="Mit KI aufteilen" />
                  </span>
                  <textarea
                    value={hint}
                    rows={2}
                    maxLength={SUGGESTION_MAX_HINT}
                    disabled={busy}
                    onChange={(e) => setHint(e.target.value)}
                    placeholder={intl.formatMessage({ id: "match.gapListings.hintPlaceholder", defaultMessage: "Optional: Aufteilung oder gewünschte Stellenbezeichnungen, z. B. „Backend-Entwickler und UX-Designer“" })}
                    className="field-sizing-content w-full resize-none bg-transparent text-smycles-body outline-none placeholder:text-ink-muted"
                  />
                </label>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button variant="secondary" size="compact" onClick={() => void suggest()}
                    disabled={busy || !suggestionInput} aria-busy={suggesting}>
                    <FormattedMessage id="match.gapListings.suggest" defaultMessage="Rollenaufteilung vorschlagen" />
                  </Button>
                  {beforeSuggestion ? (
                    <Button variant="ghost" size="sm" disabled={busy}
                      onClick={() => { setPlan(beforeSuggestion); setBeforeSuggestion(null); }}>
                      <FormattedMessage id="match.gapListings.undoSuggestion" defaultMessage="Vorschlag verwerfen" />
                    </Button>
                  ) : null}
                </div>
                {suggesting ? (
                  <ProgressBar className="mt-4"
                    label={intl.formatMessage({ id: "match.gapListings.suggesting", defaultMessage: "Rollenaufteilung wird vorgeschlagen" })} />
                ) : null}
                {suggestionNote ? <p role="status" className="mt-2 smycles-field-meta text-ink">{suggestionNote}</p> : null}
              </div>
            ) : null}

            <Disclosure title={<FormattedMessage id="match.gapListings.descriptionLabel" defaultMessage="Beschreibung aus dem Match" />}>
              {description.trim() ? (
                <p className="whitespace-pre-line smycles-field-value text-ink">{description}</p>
              ) : (
                <p className="mt-1 smycles-field-meta text-ink-muted">
                  <FormattedMessage id="match.gapListings.noDescription" defaultMessage="Das Match hat keine Beschreibung. Die Entwürfe bleiben ohne." />
                </p>
              )}
            </Disclosure>

            {createError ? <p role="alert" className="smycles-field-meta text-ink">{createError}</p> : null}
          </div>

          <section aria-labelledby={`${uid}-gaps`} className="min-w-0 bg-page box-flat p-6 @lg:sticky @lg:top-0 @lg:max-h-[calc(90vh-9rem)] @lg:overflow-y-auto @lg:overscroll-contain">
            <BoxHeader title={<span id={`${uid}-gaps`}>
              <FormattedMessage id="match.result.analysis.gaps" defaultMessage="Lücken" />
            </span>} />
            {skillEntries.length > 0 ? (
              <MatchSkillsDiagram entries={skillEntries} readOnly
                renderEntry={(entry) => renderGap(gapByKey.get(entry.id)!, true)} />
            ) : null}
            {otherGaps.length > 0 ? (
              <ul className={skillEntries.length ? "mt-4" : undefined}>
                {otherGaps.map((gap) => <li key={gap.key} className="py-2">{renderGap(gap)}</li>)}
              </ul>
            ) : null}
          </section>
        </div>
      </div>
    </Modal>
  );
}


function GapMeta({ gap }: { gap: GapRequirement }) {
  const intl = useIntl();
  const a = gap.analysis;
  const lines: string[] = [];
  if (gap.evidence === "UNVERIFIED") {
    lines.push(intl.formatMessage(
      { id: "match.gapListings.unverified", defaultMessage: "nicht nachgewiesen: {count, plural, one {# Person} other {# Personen}} ohne Bewertung" },
      { count: a.unrated }));
  } else if (gap.evidence === "OVERSHOOT") {
    lines.push(intl.formatMessage({ id: "match.gapListings.overshoot", defaultMessage: "zu viele Träger, eine weitere Stelle schließt das nicht" }));
  }
  if (lines.length === 0) return null;
  return (
    <span className="mt-1 block smycles-field-meta text-ink-muted">
      {lines.map((line) => <span key={line} className="block">{line}</span>)}
    </span>
  );
}

function CreationLine({ creation }: { creation: RoleCreation }) {
  if (creation.state === "CREATED") {
    return (
      <p className="mt-2 smycles-field-meta text-ink">
        <FormattedMessage id="match.gapListings.created" defaultMessage="Entwurf angelegt." />{" "}
        <Link to={`/share/listings/${creation.listingId}`} className={LINK}>
          <FormattedMessage id="match.gapListings.openDraft" defaultMessage="Öffnen" />
        </Link>
      </p>
    );
  }
  if (creation.state === "UNCERTAIN") {
    return (
      <p role="alert" className="mt-2 smycles-field-meta text-ink">
        <FormattedMessage
          id="match.gapListings.uncertain"
          defaultMessage="Die Verbindung brach ab. Ob der Entwurf angelegt wurde, ist unklar. Er wird nicht noch einmal gesendet."
        />{" "}
        <Link to={LISTINGS_PATH} className={LINK}>
          <FormattedMessage id="match.gapListings.checkListings" defaultMessage="In der Verwaltung prüfen" />
        </Link>
      </p>
    );
  }
  return <p role="alert" className="mt-2 smycles-field-meta text-ink">{creation.message}</p>;
}
